import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import "../styles/dashboard.css";

import Header from "../components/Header";
import UserCard from "../components/UserCard";
import SummaryCard from "../components/SummaryCard";
import HealthScoreCard from "../components/HealthScoreCard";
import BodyMetricsCard from "../components/BodyMetricsCard";
import HealthOverviewCard from "../components/HealthOverviewCard";
import VitalsCard from "../components/VitalsCard";
import HealthOverviewChart from "../components/HealthOverviewChart";
import FinalGraph from "../components/FinalGraph";
import { formatSleep } from "../utils/format";

// ─── Token helpers ─────────────────────────────────────────────────────────────
const SESSION_KEY = "dashboard_access_token";

/**
 * Retrieves the access token using this priority:
 *   1. URL query param  ?token=...  (first load — then immediately stripped)
 *   2. sessionStorage   (subsequent renders / refreshes within the same tab)
 *
 * Why sessionStorage and not localStorage?
 *   - sessionStorage is cleared when the tab closes, so the token doesn't
 *     linger on the device longer than the session.
 *   - It is never sent to the server automatically (unlike cookies without
 *     the HttpOnly flag).
 */
function getAndStoreToken(search, navigate) {
  const params = new URLSearchParams(search);
  const tokenFromUrl = params.get("token");

  if (tokenFromUrl) {
    // 1. Persist to sessionStorage immediately
    sessionStorage.setItem(SESSION_KEY, tokenFromUrl);

    // 2. Strip the token (and any other sensitive params) from the URL
    //    replace:true so the "dirty" URL is removed from browser history.
    params.delete("token");
    const cleanSearch = params.toString();
    navigate(
      { search: cleanSearch ? `?${cleanSearch}` : "" },
      { replace: true }
    );

    return tokenFromUrl;
  }

  // No token in URL — try sessionStorage (page refresh / component remount)
  return sessionStorage.getItem(SESSION_KEY);
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [selectedTab, setSelectedTab]   = useState("heart");
  const [patientData, setPatientData]   = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");

  const { search } = useLocation();
  const navigate   = useNavigate();

  const queryParams  = new URLSearchParams(search);
  const userId       = queryParams.get("userId");
  const [days, setDays] = useState(Number(queryParams.get("days")) || 14);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // ── Get token safely (strips it from URL as a side-effect) ──
        const access_token = getAndStoreToken(search, navigate);

        if (!access_token) {
          setError("Session expired or no token provided. Please log in again.");
          return;
        }

        const response = await axios.get(
          `https://api.future-health.ai/api/patients/health/trends`,
          {
            params:  { trend_type: "future_lynk" },
            headers: { Authorization: `Bearer ${access_token}` },
          }
        );

        setPatientData(response.data.data.result);

      } catch (err) {
        console.error("API error:", err);
        setError("Unable to fetch patient data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Only re-run if the URL search string changes (e.g. a new token is injected)
  }, [search]);

  // ── Chart helpers ────────────────────────────────────────────────────────────
  const getWeekdayLabel = (dateStr) => {
    const dayIndex = new Date(dateStr).getDay();
    return ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][dayIndex];
  };

  const parseVitalsToChart = (data) => {
    if (!data || !data.health_metrics) return [];

    const m    = data.health_metrics;
    const date = new Date().toISOString().slice(0, 10);

    return [
      {
        date,
        label:    new Date(date).toLocaleDateString("en-US", { weekday: "short" }),
        steps:    m.steps_data ?? 0,
        heartRate: m.heart_rate?.value ?? null,
        spo2:     m.spo2?.value ?? null,
        bpSys:    m.bp?.systolic ?? null,
        bpDia:    m.bp?.diastolic ?? null,
        sleepHrs: m.sleep_data ? Math.round(m.sleep_data / 3600) : null
      }
    ];
  };

  const chartVitals = parseVitalsToChart(patientData);
  const profile     = patientData?.patient?.health_profile;
  const metrics     = patientData?.health_metrics;

  const patientInfo = {
    patientId: profile?.patient_id,
    age:       profile?.age,
    gender:    profile?.gender,
    weight:    profile?.weight,
    height:    profile?.height,
    scores: {
      ecg:        metrics?.ecg?.score,
      bp:         metrics?.bp?.score,
      heartRate:  metrics?.heart_rate?.score,
      spo2:       metrics?.spo2?.score,
      bmi:        metrics?.bmi?.score,
      heartSound: metrics?.heart_sound?.score,
      lungSound:  metrics?.lung_sound?.score
    }
  };

  return (
    <div className="dashboard">
      <Header />

      {error   && <p className="error-message">{error}</p>}
      {loading && <p>Loading data...</p>}

      {patientData && !loading && (
        <>
          <div className="top-section">
            <UserCard        data={patientData} patientInfo={patientInfo} />
            <SummaryCard     summary={patientData?.overallSummary} />
            <HealthScoreCard data={patientData} patientInfo={patientInfo} />
          </div>

          <div className="middle-section">
            <BodyMetricsCard data={patientData} patientInfo={patientInfo} />
            <HealthOverviewCard
              data={patientData}
              onTabChange={(tab) => setSelectedTab(tab)}
            />
            <VitalsCard
              data={patientData}
              patientInfo={patientInfo}
              selectedTab={selectedTab}
            />
          </div>
        </>
      )}
    </div>
  );
}